import { Endpoint } from '@business/api/endpoint';
import {
  CompactLibraryFolderResponse,
  CompactLibraryItemResponse,
  FolderId,
  ItemId,
  LibraryFolderStructureResponse,
  LibraryItemResponse,
  LibraryItemShareItem,
  LibraryItemOptionResponse,
  LibraryItemShareType,
  LibraryItemsResponse,
  LibraryItemType,
  LibraryItemVersionResponse,
  LibraryResponse,
  LibraryVersionId,
  LibraryItemStatus,
} from '@business/entities/library';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';

export function getItems(parentId?: FolderId, withContent = false) {
  return Endpoint.get<LibraryItemsResponse>(`@gc/library`, { params: { parentId, withContent } });
}

export function getFolders() {
  return Endpoint.get<CompactLibraryFolderResponse[]>(`@gc/library/folders`);
}

/** Folders **/
export function createFolder(name: string, parentId?: FolderId) {
  return Endpoint.post<LibraryResponse>(`@gc/library`, { data: { name, parentId } });
}

export function renameFolder(id: FolderId, name: string) {
  return Endpoint.patch<LibraryResponse>(`@gc/library/${id}`, { data: { name } });
}
export function deleteFolder(id: FolderId) {
  return Endpoint.delete<void>(`@gc/library/${id}`);
}

export function getFolder(id: ItemId) {
  return Endpoint.get<LibraryItemResponse>(`@gc/library/folder/${id}`);
}

/** Items **/

export function getItem(id: ItemId) {
  return Endpoint.get<LibraryItemResponse>(`@gc/library/item/${id}`);
}

export function getItemPublic(id: ItemId) {
  return Endpoint.get<LibraryItemResponse>(`@gc/public/library/item/${id}`);
}

export function download(id: ItemId, filename: string) {
  return Endpoint.get<LibraryItemResponse>(`@gc/library/download/${id}`, { isFile: true, filename });
}

export function fileDownload(id: ItemId, filename: string) {
  return Endpoint.get<LibraryItemResponse>(`@gc/library/file-download/${id}`, { isFile: true, filename });
}

export function createItem(data: Partial<LibraryItemResponse>, parentId?: FolderId) {
  data.parentFolderId = parentId;
  if (data.type == LibraryItemType.orgChart || data.type == LibraryItemType.flowChart || data.type == LibraryItemType.custom) {
    return Endpoint.post<LibraryItemResponse[]>(`@gc/library/item`, { data });
  }
  const formData = CommonService.toFormData(data);
  return Endpoint.post<LibraryItemResponse[]>(`@gc/library/item`, { formData, hasFiles: true });
}

export function duplicateItem(id: ItemId, title: string) {
  return Endpoint.post<LibraryItemResponse>(`@gc/library/duplicate/${id}`, { data: { title } });
}

export function updateItem(data: Partial<LibraryItemResponse>, itemId: ItemId) {
  if (data.type === LibraryItemType.orgChart || data.type === LibraryItemType.flowChart || data.type == LibraryItemType.custom) {
    return Endpoint.post<LibraryItemResponse>(`@gc/library/update-item/${itemId}`, { data });
  }
  const formData = CommonService.toFormData(data);
  return Endpoint.post<LibraryItemResponse>(`@gc/library/update-item/${itemId}`, { formData, hasFiles: true });
}

export function shareItem(id: ItemId, items: LibraryItemShareItem[] = [], type: LibraryItemShareType = LibraryItemShareType.item) {
  return Endpoint.post<LibraryItemResponse | LibraryResponse>(`@gc/library/share/${id}`, { data: { items, type } });
}

export function deleteItem(id: ItemId) {
  return Endpoint.delete<void>(`@gc/library/item/${id}`);
}
export function search(search: string, type: LibraryItemType, parentId?: FolderId, requestId?: number) {
  const params = R.objectCompact({ search, parentId, type });
  return Endpoint.get<CompactLibraryItemResponse[]>(`@gc/library/search`, { params, headers: { requestId: R.toString(requestId || 1) } });
}

export function removeFile(id: ItemId, index: number) {
  return Endpoint.post<CompactLibraryItemResponse>(`@gc/library/remove-attachment/${id}`, { params: { index } });
}

export function folderStructure(folderId: FolderId) {
  return Endpoint.get<LibraryFolderStructureResponse>(`@gc/library/folder-structure`, { params: { folderId } });
}

export function move(id: string, type: LibraryItemType, destinationId: string) {
  return Endpoint.post<void>(`@gc/library/move`, { data: { id, type, destinationId } });
}

export function getLibraryItems(type: string) {
  return Endpoint.get<CompactLibraryItemResponse[]>(`@gc/library/items`, { params: { type } });
}

/** Same items as `getLibraryItems`, but only id + name; load the chosen one's content with `getLibraryItemContent`. */
export function getLibraryItemOptions(type: string) {
  return Endpoint.get<LibraryItemOptionResponse[]>(`@gc/library/items`, { params: { type, view: 'options' } });
}

export function getLibraryItemContent(id: ItemId) {
  return Endpoint.get<CompactLibraryItemResponse>(`@gc/library/item-content/${id}`);
}

export function renameVersion(id: LibraryVersionId, title: string) {
  return Endpoint.patch<LibraryItemVersionResponse[]>(`@gc/library/version/${id}`, { data: { title } });
}

export function deleteVersion(id: LibraryVersionId) {
  return Endpoint.delete<LibraryItemVersionResponse[]>(`@gc/library/version/${id}`);
}

export function onStatusChange(id: ItemId, status: LibraryItemStatus) {
  return Endpoint.post<LibraryItemResponse>(`@gc/library/change-status/${id}`, { data: { status } });
}

export function syncToAI98(id: ItemId) {
  return Endpoint.post<LibraryItemResponse>(`@gc/library/sync-to-ai98/${id}`);
}
